import Box from '@components/base/Box'
import { DateTimeInput } from '@components/inputs/DateTimeInput'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { SearchInput } from '@components/inputs/SearchInput'
import { TextAreaInput } from '@components/inputs/TextAreaInput'
import { TextInput } from '@components/inputs/TextInput'
import {
  EventDetails,
  EventFundraising,
  EventType,
} from '@graphql/__generated/graphql'
import CreateEventLayout from '@layouts/CreateEventLayout'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import moment from 'moment'
import { useTheme } from '@libs/useTheme'
import { FileInput } from '@components/inputs/FileInput'
import useFileUploader from '@libs/useFileUploader'
import { isFieldRequired, shouldShowField } from '@libs/Utility/validation'
import RichTextEditor from '@components/inputs/RichTextEditor/RichTextEditor'
import { useAuth } from '@libs/Auth/useAuth'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import { RadioFieldInput } from '@components/inputs/RadioFieldInput'
import { debounce } from '@libs/Utility/util'
import { isArray } from 'lodash'
import {
  getBrowserTimeZoneId,
  getEventTimeZone,
  getTimeZoneOptions,
  instantToPickerDate,
  isValidTimeZoneId,
  resolveLegacyTimeZoneId,
} from '@libs/EventTime'
import { setEventTimeZoneId } from '@libs/CreateEvent/eventDateTime'

const DetailsForm = () => {
  const uploadFile = useFileUploader()
  const { theme } = useTheme()
  const { profile } = useAuth()
  const { profile: organisationProfile } = useOrganisationProfile(
    profile?.company ?? profile?.university!
  )
  const { formData, setFormData, validationRules, errors, setErrors } =
    useEventEditor()

  const initialTimeZoneId =
    formData?.event?.timeZoneId ??
    resolveLegacyTimeZoneId(formData?.event?.timeZone) ??
    (formData?.event?.timeZone ? undefined : getBrowserTimeZoneId())

  const [eventDetailsData, setEventDetailsData] = useState<
    | Partial<
        EventDetails & {
          fundraising?: Partial<EventFundraising>
        }
      >
    | undefined
  >({
    ...formData?.event,
    timeZoneId: initialTimeZoneId,
    fundraising: formData?.fundraising ?? {},
  })
  const [timeZoneError, setTimeZoneError] = useState<string>()

  const eventTimeZone =
    getEventTimeZone(eventDetailsData) ?? getBrowserTimeZoneId()
  const hasUnresolvedLegacyTimeZone =
    !!eventDetailsData?.timeZone &&
    !eventDetailsData.timeZoneId &&
    !resolveLegacyTimeZoneId(eventDetailsData.timeZone)
  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions(eventDetailsData?.startDateTime),
    [eventDetailsData?.startDateTime]
  )
  const selectedTimeZoneOption = useMemo(
    () =>
      timeZoneOptions.find(
        ({ value }) => value === eventDetailsData?.timeZoneId
      ),
    [eventDetailsData?.timeZoneId, timeZoneOptions]
  )

  const internalLocationsOptions = (organisationProfile?.locations ?? []).map(
    (loc) => ({ label: loc, value: loc })
  )

  useEffect(() => {
    const persisted = formData?.event
    if (!persisted) return

    const schedule = {
      timeZoneId:
        persisted.timeZoneId ??
        resolveLegacyTimeZoneId(persisted.timeZone) ??
        eventDetailsData?.timeZoneId ??
        (persisted.timeZone ? undefined : getBrowserTimeZoneId()),
      startDateTime: persisted.startDateTime,
      entryDateTime: persisted.entryDateTime,
      endDateTime: persisted.endDateTime,
      registrationCloseDateTime: persisted.registrationCloseDateTime,
    }
    setEventDetailsData((current) => {
      if (
        current &&
        Object.entries(schedule).every(
          ([key, value]) => current[key as keyof typeof current] === value
        )
      ) {
        return current
      }
      return { ...current, ...schedule }
    })
  }, [
    formData?.event?.timeZoneId,
    formData?.event?.timeZone,
    formData?.event?.startDateTime,
    formData?.event?.entryDateTime,
    formData?.event?.endDateTime,
    formData?.event?.registrationCloseDateTime,
  ])

  useEffect(() => {
    if (!!eventDetailsData) {
      delete eventDetailsData.eventType
      const { fundraising, ...eventData } = eventDetailsData
      const payload = {
        ...formData,
        event: { ...formData?.event, ...eventData } as EventDetails,
        ...(fundraising
          ? {
              fundraising: {
                ...formData?.fundraising,
                ...fundraising,
              },
            }
          : {}),
      }
      //@ts-ignore
      if (payload?.title || payload?.event?.title) {
        debouncedSave({ formData: payload, errors })
      }
    }
  }, [eventDetailsData])

  const debouncedSave = useCallback(
    debounce((input: { formData: any; errors: any }) => {
      const hasErrors =
        !!input?.errors && isArray(input?.errors) && input?.errors?.length > 0

      if (!hasErrors && !!input?.formData)
        saveDetails({
          ...input.formData,
        })
    }, 1000),
    []
  )

  const saveDetails = async (payload: any) => {
    setFormData(payload)
  }

  const errorHandler =
    (id: string, alert: boolean = false) =>
    (message: string, testId: string) => {
      const payload = [...errors]

      const hasError = !!message || message !== ''
      const isCleared = payload.find((f) => f.eleId === testId) && !hasError

      if (isCleared) return setErrors(payload.filter((f) => f.eleId !== testId))

      const isDuplicate = !!payload.find((p) => p.eleId === testId)

      const newError = {
        step: id ?? 'unknown',
        message,
        eleId: testId,
        alert,
      }

      if (!isDuplicate && hasError) setErrors([...payload, newError])
    }

  return (
    <Box className="flex w-full space-x-12">
      <Box className="w-full">
        <Box className={`flex flex-col w-full -mb-2`}>
          <TextInput
            tooltip="Event title is required to publish"
            labelBgColour={theme.backgroundColourSecondary}
            testid={'name-input'}
            validations={{
              maxLength: {
                value: 150,
                message: 'Title must be 150 characters or less',
              },
            }}
            required={isFieldRequired(
              validationRules.find((f) =>
                f[0].includes(
                  eventDetailsData?.eventType === EventType.Fundraiser
                    ? 'fundraising_title'
                    : 'event_title'
                )
              )
            )}
            hint="(Maximum of 150 characters)"
            label="Title"
            placeholder={
              eventDetailsData?.eventType === EventType.Fundraiser
                ? 'Fundraiser Name'
                : 'Event Name'
            }
            className="flex-1 mr-2"
            value={
              eventDetailsData?.eventType === EventType.Fundraiser
                ? eventDetailsData?.fundraising?.title
                : eventDetailsData?.title
            }
            onChange={(data) => {
              setEventDetailsData((state) => {
                return {
                  ...state,
                  ...(state?.eventType === EventType.Fundraiser
                    ? {
                        title: data,
                        fundraising: {
                          ...state?.fundraising,
                          title: data,
                        },
                      }
                    : { title: data }),
                }
              })
            }}
          />
          <TextInput
            tooltip="A short event description is required to publish"
            labelBgColour={theme.backgroundColourSecondary}
            testid={'short-description-input'}
            validations={{
              maxLength: {
                value: 300,
                message: 'Description must be 300 characters or less',
              },
            }}
            required={isFieldRequired(
              validationRules.find((f) =>
                f[0].includes('event_shortDescription')
              )
            )}
            setError={errorHandler('event')}
            hint="(Maximum of 300 characters)"
            label="Short Description"
            placeholder={`Write a short description of your ${
              eventDetailsData?.eventType === EventType.Fundraiser
                ? 'fundraiser'
                : 'event'
            }`}
            className="flex-1 ml-2 sm:ml-0 mt-1"
            value={eventDetailsData?.shortDescription}
            onChange={(data) => {
              setEventDetailsData({
                ...(eventDetailsData ?? {}),
                shortDescription: data,
              })
            }}
          />
        </Box>

        <RichTextEditor
          tooltip="A full event description is required to publish"
          testid="description-input"
          validations={
            {
              // maxLength: {
              //   value: 3000,
              //   message: 'Description must be 3000 characters or less',
              // },
            }
          }
          className="mt-2"
          required={isFieldRequired(
            validationRules.find((f) =>
              f[0].includes(
                eventDetailsData?.eventType === EventType.Fundraiser
                  ? 'fundraising_description'
                  : 'event_description'
              )
            )
          )}
          setError={errorHandler('event')}
          // hint="(Maximum of 3000 characters)"
          label="Description"
          placeholder={`Write all of the details of your ${
            eventDetailsData?.eventType === EventType.Fundraiser
              ? 'fundraiser'
              : 'event'
          }...`}
          value={eventDetailsData?.description}
          onChange={(jsonString) => {
            if (eventDetailsData?.description !== jsonString)
              setEventDetailsData((state) => {
                const payload = {
                  ...state,
                  ...(state?.eventType === EventType.Fundraiser
                    ? {
                        description: jsonString,
                        fundraising: {
                          ...state?.fundraising,
                          description: jsonString,
                        },
                      }
                    : { description: jsonString }),
                }
                return payload
              })
          }}
        />
        {/* Internal location control */}
        {internalLocationsOptions && internalLocationsOptions.length > 0 && (
          <Box className="mb-16 -mt-6">
            <RadioFieldInput
              testid={'internal-or-exteral-input'}
              hint={`Select this option if the event is taking place within the university`}
              options={['Yes', 'No']}
              label={`Is this an internal location?`}
              className={`text-xl ml-[1px]`}
              value={eventDetailsData?.isInternal ? 'Yes' : 'No'}
              onChange={(data) => {
                setEventDetailsData((state) => ({
                  ...state,
                  isInternal: data === 'Yes',
                }))
              }}
            />
            {eventDetailsData?.isInternal && (
              <Box className="mt-4">
                <DropdownInput
                  value={eventDetailsData?.internalLocation}
                  label="Location"
                  options={internalLocationsOptions}
                  onChange={(data) => {
                    setEventDetailsData((state) => ({
                      ...state,
                      internalLocation: data,
                    }))
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <TextAreaInput
          tooltip="An event location is required to publish"
          show={shouldShowField(
            validationRules.find((f) => f[0].includes('event_eventLocation'))
          )}
          testid={'location-input'}
          label="Event Location"
          placeholder="Please input the address where the event take place"
          required={isFieldRequired(
            validationRules.find((f) => f[0].includes('event_eventLocation'))
          )}
          setError={errorHandler('event')}
          className="-mt-6"
          value={eventDetailsData?.eventLocation}
          onChange={(data) => {
            setEventDetailsData((state) => ({
              ...state,
              eventLocation: data,
            }))
          }}
        />
        <RadioFieldInput
          testid={'visibility-input'}
          hint={`(Private ${
            eventDetailsData?.eventType === EventType.Fundraiser
              ? 'fundraisers'
              : 'events'
          } are only visible to your organisation)`}
          options={['Private', 'Public']}
          label={`${
            eventDetailsData?.eventType === EventType.Fundraiser
              ? 'Fundraiser'
              : 'Event'
          } Visibility`}
          className={`my-8 text-lg ml-[1px]`}
          value={
            eventDetailsData?.publiclyListed === false ? 'Private' : 'Public'
          }
          onChange={(data) => {
            setEventDetailsData((state) => ({
              ...state,
              publiclyListed: data === 'Public',
            }))
          }}
        />
        {/*<ColorPicker label="Event Main Colour" testid={'color-picker'} />
          <ColorPicker
            label="Event Background Colour"
            testid={'bg-color-picker'}
            onChange={() => setHasEdited(true)}
          />*/}
        {/* <ColorPicker label="Event Text Colour" /> */}
        <Box>
          <Box className="mt-8 flex justify-between flex-col">
            <Box className="text-lg" textColour="textColour">
              Select Date and Time
            </Box>
            <Box className="mt-2">
              <SearchInput
                value={selectedTimeZoneOption?.label ?? ''}
                label="Timezone"
                placeholder="Search IANA timezones"
                options={timeZoneOptions}
                onChange={(data) => {
                  if (!isValidTimeZoneId(data)) return
                  try {
                    const updated = setEventTimeZoneId(eventDetailsData, data)
                    setTimeZoneError(undefined)
                    setEventDetailsData(updated)
                  } catch (error) {
                    setTimeZoneError(
                      error instanceof Error
                        ? error.message
                        : 'Unable to apply that timezone.'
                    )
                  }
                }}
              />
              <p className="mt-1 text-sm opacity-75">
                Your timezone is used only as the initial default. Search and
                select any timezone for the event.
              </p>
              {timeZoneError && (
                <p className="mt-2 text-sm text-red-500" role="alert">
                  {timeZoneError}
                </p>
              )}
              {hasUnresolvedLegacyTimeZone && (
                <p className="mt-2 text-sm text-amber-500" role="alert">
                  This event uses the legacy fixed offset{' '}
                  {eventDetailsData.timeZone}. Select an IANA timezone before
                  publishing; no location has been guessed.
                </p>
              )}
            </Box>
          </Box>
          <Box className="flex flex-col sm:flex-row justify-between sm:space-x-6 mt-2">
            <DateTimeInput
              tooltip="Start date/time is required to publish"
              labelBgColour={theme.backgroundColourSecondary}
              label="Event Start"
              id={'start-time-input'}
              testid={'start-time-input'}
              required={isFieldRequired(
                validationRules.find((f) =>
                  f[0].includes('event_startDateTime')
                )
              )}
              value={eventDetailsData?.startDateTime}
              onChange={(data) => {
                setEventDetailsData((state) => ({
                  ...state,
                  startDateTime: data,
                }))
              }}
              className="flex-1"
              filterDate={(rawDate) => {
                const date = moment(rawDate)
                const today = moment(
                  instantToPickerDate(new Date().toISOString(), eventTimeZone)
                )
                if (date.isBefore(today, 'day')) return false
                return true
              }}
              filterTime={() => {
                return true
              }}
              clickIdOnChange="end-time-input"
              timeZoneId={eventTimeZone}
            />
            <DateTimeInput
              timeZoneId={eventTimeZone}
              tooltip="End date/time is required to publish"
              labelBgColour={theme.backgroundColourSecondary}
              id={'end-time-input'}
              testid={'end-time-input'}
              value={eventDetailsData?.endDateTime}
              validations={{
                greaterThanDate: eventDetailsData?.startDateTime ?? '',
              }}
              required={isFieldRequired(
                validationRules.find((f) => f[0].includes('event_endDateTime'))
              )}
              className="flex-1"
              label="Event End"
              filterDate={(rawDate) => {
                const date = moment(rawDate)
                const startTime = instantToPickerDate(
                  eventDetailsData?.startDateTime,
                  eventTimeZone
                )

                if (!!startTime && date.isBefore(startTime, 'day')) return false
                return true
              }}
              filterTime={(rawTime) => {
                const time = moment(rawTime)
                const startTime = moment(
                  instantToPickerDate(
                    eventDetailsData?.startDateTime,
                    eventTimeZone
                  )
                )

                if (!!startTime && time.isSameOrBefore(startTime, 'minute'))
                  return false
                return true
              }}
              onChange={(data) => {
                setEventDetailsData((state) => ({
                  ...state,
                  endDateTime: data,
                }))
              }}
              initialDate={
                instantToPickerDate(
                  eventDetailsData?.registrationCloseDateTime ??
                    eventDetailsData?.startDateTime,
                  eventTimeZone
                ) ?? undefined
              }
            />
            <DateTimeInput
              tooltip="The time attendees can access the event"
              labelBgColour={theme.backgroundColourSecondary}
              label="Entry Time"
              id={'entry-time-input'}
              testid={'entry-time-input'}
              required={isFieldRequired(
                validationRules.find((f) =>
                  f[0].includes('event_entryDateTime')
                )
              )}
              value={eventDetailsData?.entryDateTime}
              onChange={(data) => {
                setEventDetailsData((state) => ({
                  ...state,
                  entryDateTime: data,
                }))
              }}
              className="flex-1"
              filterDate={(rawDate) => {
                const date = moment(rawDate)

                const startTime = moment(
                  instantToPickerDate(
                    eventDetailsData?.startDateTime,
                    eventTimeZone
                  )
                )

                if (date.isSameOrBefore(startTime, 'day')) return true
                return false
              }}
              filterTime={(rawTime) => {
                const date = moment(rawTime)
                const startTime = moment(
                  instantToPickerDate(
                    eventDetailsData?.startDateTime,
                    eventTimeZone
                  )
                )
                if (date.isSameOrBefore(startTime, 'minute')) return true
                return false
              }}
              clickIdOnChange="registration-close-time-input"
              timeZoneId={eventTimeZone}
            />
            <DateTimeInput
              timeZoneId={eventTimeZone}
              tooltip="Registration close date/time is required to publish"
              labelBgColour={theme.backgroundColourSecondary}
              id={'registration-close-time-input'}
              testid={'egistration-close-time-input'}
              show={eventDetailsData?.eventType !== EventType.Fundraiser}
              value={eventDetailsData?.registrationCloseDateTime}
              required={isFieldRequired(
                validationRules.find((f) =>
                  f[0].includes('event_registrationCloseDateTime')
                )
              )}
              onChange={(data) => {
                setEventDetailsData((state) => ({
                  ...state,
                  registrationCloseDateTime: data,
                }))
              }}
              className="flex-1"
              label="Registration Close"
              filterDate={(rawDate) => {
                const date = moment(rawDate)
                const endTime = moment(
                  instantToPickerDate(
                    eventDetailsData?.endDateTime,
                    eventTimeZone
                  )
                )

                if (!!endTime && date.isAfter(endTime, 'day')) return false
                return true
              }}
              filterTime={(rawTime) => {
                const time = moment(rawTime)
                const endTime = moment(
                  instantToPickerDate(
                    eventDetailsData?.endDateTime,
                    eventTimeZone
                  )
                )

                if (!!endTime && time.isAfter(endTime, 'minute')) return false
                return true
              }}
              clickIdOnChange="end-time-input"
              initialDate={
                instantToPickerDate(
                  eventDetailsData?.startDateTime,
                  eventTimeZone
                ) ?? undefined
              }
            />
          </Box>
        </Box>
        <Box className="text-lg mt-6" textColour="textColour">
          Event
          <span className="text-xs ml-2">
            (Optional: Image should be square and at least 800x800px)
          </span>
        </Box>
        <Box className="flex flex-wrap mt-2">
          <FileInput
            uploadFile={uploadFile}
            label="Event Image"
            hint=""
            crop={true}
            cropAspectRatio={1}
            cropShape={'rect'}
            testid={'banner-upload'}
            value={eventDetailsData?.bannerImage!}
            onChange={(data) => {
              setEventDetailsData((state) => ({
                ...state,
                bannerImage: data as string,
              }))
            }}
          />
          {/* <FileInput */}
          {/*   uploadFile={uploadFile} */}
          {/*   label="Logo" */}
          {/*   crop={true} */}
          {/*   cropAspectRatio={1} */}
          {/*   cropShape={'rect'} */}
          {/*   data-testid={'thumbnail-upload'} */}
          {/*   value={eventDetailsData?.thumbnailImage!} */}
          {/*   onChange={(data) => { */}
          {/*     setEventDetailsData({ */}
          {/*       ...formData?.event, */}
          {/*       ...(eventDetailsData ?? {}), */}
          {/*       thumbnailImage: data as string, */}
          {/*     }) */}
          {/*   }} */}
          {/* /> */}
        </Box>
      </Box>
    </Box>
  )
}

DetailsForm.Layout = CreateEventLayout

export default DetailsForm
